import { createClient, SupabaseClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://wlhvliyrwypvllcfttjp.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndsaHZsaXlyd3lwdmxsY2Z0dGpwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU1ODE5MjAsImV4cCI6MjA5MTE1NzkyMH0.E3dwJkAW3mLR4vZKNWkUuumAORcdqFh55jKwKxqr-DI';

let supabase: SupabaseClient;

function getClient(): SupabaseClient {
  if (!supabase) {
    supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  }
  return supabase;
}

export interface PlayerRecord {
  id: string;
  name: string;
  created_at: string;
}

export interface ScoreRecord {
  id: string;
  player_id: string;
  player_name: string;
  stage_number: number;
  stage_score: number;
  total_score: number;
  time_remaining: number;
  lives_remaining: number;
  completed: boolean;
  created_at: string;
}

export interface LeaderboardEntry {
  player_name: string;
  best_score: number;
  highest_stage: number;
  created_at: string;
}

export interface StageScoreRecord {
  id: string;
  player_id: string;
  player_name: string;
  stage_number: number;
  stage_score: number;
  time_remaining: number;
  created_at: string;
}

/**
 * Register a new player (arcade-style, no auth)
 */
export async function registerPlayer(name: string): Promise<PlayerRecord | null> {
  const { data, error } = await getClient()
    .from('players')
    .insert({ name })
    .select()
    .single();

  if (error) {
    console.error('Failed to register player:', error);
    return null;
  }
  return data;
}

/**
 * Submit a score entry
 */
export async function submitScore(params: {
  playerId: string;
  playerName: string;
  stageNumber: number;
  stageScore: number;
  totalScore: number;
  timeRemaining: number;
  livesRemaining: number;
  completed: boolean;
}): Promise<ScoreRecord | null> {
  const { data, error } = await getClient()
    .from('scores')
    .insert({
      player_id: params.playerId,
      player_name: params.playerName,
      stage_number: params.stageNumber,
      stage_score: params.stageScore,
      total_score: params.totalScore,
      time_remaining: params.timeRemaining,
      lives_remaining: params.livesRemaining,
      completed: params.completed,
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to submit score:', error);
    return null;
  }
  return data;
}

/**
 * Submit a per-stage result. One row per cleared stage (and boss win).
 * Feeds the per-stage leaderboard only — kept out of the `scores` table so
 * the CSV / top_scores stay one row per run.
 */
export async function submitStageScore(params: {
  playerId: string;
  playerName: string;
  stageNumber: number;
  stageScore: number;
  timeRemaining: number;
}): Promise<void> {
  const { error } = await getClient()
    .from('stage_scores')
    .insert({
      player_id: params.playerId,
      player_name: params.playerName,
      stage_number: params.stageNumber,
      stage_score: params.stageScore,
      time_remaining: params.timeRemaining,
    });

  if (error) {
    console.error('Failed to submit stage score:', error);
  }
}

/**
 * Get global top scores
 */
export async function getGlobalLeaderboard(limit: number = 10): Promise<LeaderboardEntry[]> {
  const { data, error } = await getClient()
    .from('high_scores')
    .select('*')
    .limit(limit);

  if (error) {
    console.error('Failed to fetch leaderboard:', error);
    return [];
  }
  return data || [];
}

/**
 * Get top scores for a specific stage. Reads the dedicated stage_scores
 * table (one row per cleared stage / boss win), independent of the run-level
 * `scores` table that feeds the CSV.
 */
export async function getStageLeaderboard(stageNumber: number, limit: number = 10): Promise<StageScoreRecord[]> {
  const { data, error } = await getClient()
    .from('stage_scores')
    .select('*')
    .eq('stage_number', stageNumber)
    .order('stage_score', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Failed to fetch stage leaderboard:', error);
    return [];
  }
  return data || [];
}

/**
 * Get a player's rank on the global leaderboard
 */
export async function getPlayerRank(totalScore: number): Promise<number> {
  const { count, error } = await getClient()
    .from('scores')
    .select('*', { count: 'exact', head: true })
    .gt('total_score', totalScore);

  if (error) {
    console.error('Failed to get player rank:', error);
    return -1;
  }
  return (count || 0) + 1;
}
